import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map, List, fromJS } from 'immutable'
import _get from 'lodash/get'
import _round from 'lodash/round'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from 'actions'
import { connect as connectStaticText } from 'components/StaticText/connect'
import { get as getConfig } from 'config'
import { H4, H6 } from 'components/Heading'
import Button from 'components/Button'
import {
  AUTHENTICATED_PLAN_SKUS,
  formatPrice,
  PLAN_SUBSCRIPTION_CANCELLED,
  PLAN_TYPE_UPGRADE,
  PLAN_TYPE_DOWNGRADE,
  PLAN_ID_COMPLIMENTARY,
  determinePlanUpgradeDowngrade,
  determinePlanUpgradeDowngradeV2,
  getPlanSubscriptionType,
  getActiveProductPlanRateId,
  getCurrentPlanPrice,
} from 'services/plans'
import {
  format as formatDateTime,
  getDateTime,
  getDateLocale,
  ordinalDateFormatingi18n,
  formatWithLocale,
  getCurrentTime,
} from 'services/date-time'
import {
  URL_ACCOUNT,
  URL_ACCOUNT_UPDATE_PAYMENT,
} from 'services/url/constants'
import { hasScheduledPause, getUserAccountChangePlanProratePrice } from 'services/user-account'
import { formatTracking, CHECKOUT_ORDER_ERROR_TYPE_PAYMENT } from 'services/checkout'
import { getPrimary } from 'services/languages'
import { EUR, CAD } from 'services/currency'
import { FR } from 'services/languages/constants'
import { getPlanSimpleTitle } from 'components/PlanCardGrid/utils'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])
const changePlanProratePriceActive = !!_get(config, ['features', 'userAccount', 'changePlanProratePrice'])

function MyAccountChangePlanModal (props) {
  const {
    staticText,
    dismissModal,
    updateUserAccountPlan,
    resetAccountChangePlanData,
    userAccount,
    plans,
    user,
    auth,
    currentPlan = Map(),
  } = props

  // Local state for prorate price data
  const [proratePriceData, setProratePriceData] = useState(null)
  const [isProratePriceLoading, setIsProratePriceLoading] = useState(false)
  let userCurrentPlanCost = getCurrentPlanPrice(userAccount)
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const currentPlanSku = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planSku'])
  let plansData = List()

  if (!planGraphQLActive) {
    plansData = plans.getIn(['data', 'plans'], List()) || List()
  }

  const selectedPlanSku = plans.getIn(['changePlanData', 'selectedPlan'])
  let selectedPlan = selectedPlanSku ? plans.getIn(['data', 'plans'], List()).find((plan) => {
    return plan.get('sku') === selectedPlanSku
  }) : Map()

  if (planGraphQLActive) {
    selectedPlan = plans.getIn(['changePlanData', 'selectedPlan'])
  }

  const planType = selectedPlan.get('heading')
  const currency = selectedPlan.get('currencyIso')
  const billingSchedule = selectedPlan.get('billingSchedule')
  const cost = (selectedPlan.get('costs', List()) || List()).last()
  const price = formatPrice(currency, cost, false, null, userLanguage)
  const selectedPlanId = selectedPlan.get('id')
  const selectedPlanSubscriptionType = getPlanSubscriptionType(selectedPlanId)
  const currentPlanId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'productRatePlanId'])
  const nextPlanId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'nextPlan', 'productRatePlanId'])
  const isInComp = currentPlanId === PLAN_ID_COMPLIMENTARY
  const planId = isInComp ? nextPlanId : currentPlanId
  const userCurrentRatePlanId = getActiveProductPlanRateId(userAccount)
  const userCurrentPlan = planGraphQLActive ? currentPlan : plansData.find((p) => p.get('id') === userCurrentRatePlanId) || Map()
  const userCurrentPlanType = userCurrentPlan.get('legacyType')
  const selectedPlanType = selectedPlan.get('legacyType')
  const planUpgradeDownGradeType = planGraphQLActive
    ? determinePlanUpgradeDowngradeV2(userCurrentPlanType, selectedPlanType)
    : determinePlanUpgradeDowngrade(selectedPlanId, planId)
  const isScheduledPause = hasScheduledPause(userAccount)
  const cancelDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'cancelDate'])
  const endDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'endDate'])
  const today = getCurrentTime()
  const cancellationScheduled = cancelDate && (new Date(endDate) >= today)
  const subscriptionStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status'])
  let nextBillDate = null
  const planHeading = getPlanSimpleTitle({ planType: selectedPlanSubscriptionType, staticText })
    || planType
  const planIsCurrentPlan = userCurrentRatePlanId === selectedPlanId
  const planAlreadySelected = planIsCurrentPlan && !cancellationScheduled
  const userCurrentPlanCancelScheduled = planIsCurrentPlan && cancellationScheduled
  const useCurrentPlanPrice = planAlreadySelected || userCurrentPlanCancelScheduled
  const userPlanIsLegacyStandardPlan = currentPlanSku
    && AUTHENTICATED_PLAN_SKUS.includes(currentPlanSku)
  const nextCharge = (userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'charges'], List()) || List()).last() || Map()
  const nextChargeAmount = nextCharge.get('price')

  // If the person is in a legacy plan and this is their current plan
  if (planIsCurrentPlan && userPlanIsLegacyStandardPlan) {
    userCurrentPlanCost = nextChargeAmount
  }

  if (planGraphQLActive) {
    userCurrentPlanCost = cost
  }

  // last ditch effort, just show a price
  if (!userCurrentPlanCost) {
    userCurrentPlanCost = (userCurrentPlan.get('costs', List()) || List()).last() || 0
  }

  if (subscriptionStatus === PLAN_SUBSCRIPTION_CANCELLED) {
    nextBillDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paidThroughDate'])
  } else {
    nextBillDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'nextReviewDate'])
  }

  const userCurrentPlanPrice = formatPrice(currency, userCurrentPlanCost, false, null, userLanguage)
  const reformattedDate = getDateTime(nextBillDate)
  const dateFormatString = ordinalDateFormatingi18n(userLanguage)
  const dateLocale = getDateLocale(userLanguage)
  const formattedDate = formatWithLocale(
    formatDateTime(reformattedDate),
    dateFormatString,
    { locale: dateLocale },
  )
  const isProcessing = userAccount.getIn(['billingSubscriptionsProcessing']) || auth.get('processing')
  const showChangeError = userAccount.getIn(['data', 'billing', 'planChange', '_dataError'])
  || userAccount.getIn(['data', 'billing', 'planChange', 'success']) === false
  const errorCode = userAccount.getIn(['data', 'billing', 'planChange', 'errorCode'])
  const showPlanChanged = userAccount.getIn(['data', 'billing', 'planChange']) && !showChangeError

  useEffect(() => {
    const fetchProratePrice = async () => {
      if (
        changePlanProratePriceActive
        && planUpgradeDownGradeType === PLAN_TYPE_UPGRADE
        && !isProratePriceLoading
        && !proratePriceData
      ) {
        setIsProratePriceLoading(true)

        try {
          const res = await getUserAccountChangePlanProratePrice({
            auth,
            nextRatePlanId: selectedPlanId,
          })
          setProratePriceData(fromJS(res))
        } catch (error) {
          // Set error state so we can fall back to default description
          setProratePriceData(fromJS({
            error: true,
            success: false,
            amountWithoutTax: null,
            taxAmount: null,
            targetDate: null,
          }))
        } finally {
          setIsProratePriceLoading(false)
        }
      } else {
        // Reset state when not upgrading
        setProratePriceData(null)
        setIsProratePriceLoading(false)
      }
    }

    fetchProratePrice()
  }, [changePlanProratePriceActive, planUpgradeDownGradeType, auth, selectedPlanId])

  const renderDescriptionPlaceholder = () => {
    return (
      <div className="my-account-change-plan-modal__container">
        <div className="my-account-change-plan-modal__desciption-placeholder" />
        <div className="my-account-change-plan-modal__desciption-placeholder" />
        <div className="my-account-change-plan-modal__desciption-placeholder" />
        <div className="my-account-change-plan-modal__desciption-placeholder" />
      </div>
    )
  }

  const renderErrorState = () => {
    if (errorCode === CHECKOUT_ORDER_ERROR_TYPE_PAYMENT) {
      return (
        <React.Fragment>
          <H4 className="my-account-change-plan-modal__title my-account-change-plan-modal__title--error">{staticText.getIn(['data', 'paymentError'])}</H4>
          <div className="my-account-change-plan-modal__container">
            <p className="my-account-change-plan-modal__desciption">{staticText.getIn(['data', 'updateErrorPaymentMethodBody'])} </p>
          </div>
          <Button
            className="button button--primary my-account-change-plan-modal__button"
            url={URL_ACCOUNT_UPDATE_PAYMENT}
          >
            {staticText.getIn(['data', 'updatePaymentMethod'])}
          </Button>
        </React.Fragment>
      )
    }

    return (
      <React.Fragment>
        <H4 className="my-account-change-plan-modal__title my-account-change-plan-modal__title--error">{staticText.getIn(['data', 'error'])}</H4>
        <div className="my-account-change-plan-modal__container">
          <p className="my-account-change-plan-modal__desciption">{staticText.getIn(['data', 'updateErrorGeneralBody'])}</p>
        </div>
        <Button
          className="button button--primary my-account-change-plan-modal__button"
          disabled={isProcessing}
          onClick={() => {
            onClickChangePlanConfirm()
          }}
        >
          {isProcessing ? staticText.getIn(['data', 'changePlanButtonProcessing']) : staticText.getIn(['data', 'retry'])}
        </Button>
      </React.Fragment>
    )
  }

  const renderSuccessState = () => {
    return (
      <React.Fragment>
        <H4 className="my-account-change-plan-modal__title">{staticText.getIn(['data', 'success'])}</H4>
        <p className="my-account-change-plan-modal__desciption">{staticText.getIn(['data', 'successDescriptionBefore'])} {planHeading} {staticText.getIn(['data', 'plan'])} </p>
        <Button
          className="button button--primary my-account-change-plan-modal__button"
          url={URL_ACCOUNT}
          scrollToTop
          onClick={() => resetAccountChangePlanData()}
        >
          {staticText.getIn(['data', 'goToMyAccount'])}
        </Button>
      </React.Fragment>
    )
  }

  const renderDescription = () => {
    if (planUpgradeDownGradeType === PLAN_TYPE_UPGRADE) {
      if (changePlanProratePriceActive) {
        if (isProratePriceLoading) {
          return renderDescriptionPlaceholder()
        }

        // If we have successful prorate data, show prorated description
        if (Map.isMap(proratePriceData) && proratePriceData.get('success') && !proratePriceData.get('error')) {
          const proratePrice = proratePriceData.get('amountWithoutTax') || 0
          const prorateTaxAmount = proratePriceData.get('taxAmount') || 0

          return (
            <React.Fragment>
              <p className="my-account-change-plan-modal__desciption">
                {staticText.getIn(['data', 'changeDescriptionProratedBefore2'])} {formatPrice(currency, _round(proratePrice + prorateTaxAmount, 2), false, null, userLanguage)} {`(${staticText.getIn(['data', 'inclusiveOfTaxes'])})`} <strong>{staticText.getIn(['data', 'today'])} </strong> {staticText.getIn(['data', 'changeDescriptionProratedAfter'])}
              </p>
              {isScheduledPause && <p>{staticText.getIn(['data', 'scheduledPauseCancelled'])}</p>}
            </React.Fragment>
          )
        }

        // If API failed or no data, fall back to default description
        return (
          <React.Fragment>
            <p className="my-account-change-plan-modal__desciption">{staticText.getIn(['data', 'changeDescriptionProratedBefore'])} <strong>{staticText.getIn(['data', 'today'])} </strong> {staticText.getIn(['data', 'changeDescriptionProratedAfter'])}
            </p>
            {isScheduledPause && <p>{staticText.getIn(['data', 'scheduledPauseCancelled'])}</p>}
          </React.Fragment>
        )
      }

      return (
        <React.Fragment>
          <p className="my-account-change-plan-modal__desciption">{staticText.getIn(['data', 'changeDescriptionProratedBefore'])} <strong>{staticText.getIn(['data', 'today'])} </strong> {staticText.getIn(['data', 'changeDescriptionProratedAfter'])}
          </p>
          {isScheduledPause && <p>{staticText.getIn(['data', 'scheduledPauseCancelled'])}</p>}
        </React.Fragment>
      )
    }

    // If there is a cancel scheduled and this is the user's current plan
    if (cancellationScheduled && currentPlanId === selectedPlanId) {
      return (
        <p
          className="my-account-change-plan-modal__desciption"
        >
          {`${staticText.getIn(['data', 'timeRemaining'])} ${formattedDate}.`}
        </p>
      )
    }

    // Default fallback description for all other cases
    return (
      <React.Fragment>
        <p className="my-account-change-plan-modal__desciption">{staticText.getIn(['data', 'changeDescription'])}</p>
        {isScheduledPause && <p>{staticText.getIn(['data', 'scheduledPauseCancelled'])}</p>}
      </React.Fragment>
    )
  }

  const renderPlanContent = () => {
    const preventCurrencyIso = currency === EUR || (currency === CAD && userLanguage === FR)

    return (
      <React.Fragment>
        <H4
          className="my-account-change-plan-modal__title"
        >
          {
            cancellationScheduled
              ? staticText.getIn(['data', 'confirmSelection'])
              : staticText.getIn(['data', 'confirmPlanChangeTitle'])
          }
        </H4>
        <div className="my-account-change-plan-modal__container">
          <H6 className="my-account-change-plan-modal__text">
            {staticText.getIn(['data', 'newPlan'])} {planType}
          </H6>
          <H6 className="my-account-change-plan-modal__text">
            {staticText.getIn(['data', 'price'])} {useCurrentPlanPrice ? userCurrentPlanPrice : price}{`${!preventCurrencyIso ? ` ${currency}` : ''}`}, {billingSchedule}
          </H6>
          <div className="my-account-change-plan-modal__container">
            {renderDescription()}
          </div>
          {planUpgradeDownGradeType === PLAN_TYPE_DOWNGRADE
            ? <div className="my-account-change-plan-modal__container">
                <p className="my-account-change-plan-modal__desciption"> {staticText.getIn(['data', 'nextBillDate'])} <strong>{formattedDate}</strong></p>
              </div>
            : null
          }
        </div>
      </React.Fragment>
    )
  }

  const onClickChangePlanConfirm = () => {
    const { inboundTracking } = props
    const tracking = formatTracking(inboundTracking)
    return updateUserAccountPlan({
      nextRatePlanId: selectedPlanId,
      planSku: selectedPlan.get('sku'),
      auth,
      tracking,
      planUpgradeDownGradeType,
    })
  }

  const renderModalDefault = () => {
    return (
      <React.Fragment>
        {renderPlanContent()}
        <Button
          className="button--primary my-account-change-plan-modal__button"
          disabled={isProcessing}
          onClick={() => {
            onClickChangePlanConfirm()
          }}
        >
          {isProcessing ? staticText.getIn(['data', 'changePlanButtonProcessing']) : staticText.getIn(['data', 'confirmChanges'])}
        </Button>
        { !isProcessing
          ? <Button
            className="button--secondary my-account-change-plan-modal__button "
            onClick={() => dismissModal()}
          >
            {staticText.getIn(['data', 'cancel'])}
          </Button>
          : null
        }
      </React.Fragment>
    )
  }

  const renderChangePlanModal = () => {
    if (showPlanChanged) {
      return renderSuccessState()
    } if (showChangeError) {
      return renderErrorState()
    }
    return renderModalDefault()
  }

  return (
    <div className="my-account-change-plan-modal">
      {renderChangePlanModal()}
    </div>
  )
}

MyAccountChangePlanModal.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
  plans: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  updateUserAccountPlan: PropTypes.func.isRequired,
  setDialogOptions: PropTypes.func.isRequired,
  setOverlayCloseOnClick: PropTypes.func.isRequired,
}

export default compose(
  connectStaticText({ storeKey: 'myAccountChangePlanModal' }),
  connectRedux(
    (state) => ({
      plans: state.plans,
      userAccount: state.userAccount,
      inboundTracking: state.inboundTracking,
      auth: state.auth,
      user: state.user,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        dismissModal: actions.dialog.dismissModal,
        updateUserAccountPlan: actions.userAccount.updateUserAccountPlan,
        setDialogOptions: actions.dialog.setDialogOptions,
        setOverlayCloseOnClick: actions.dialog.setOverlayCloseOnClick,
        resetAccountChangePlanData: actions.userAccount.resetAccountChangePlanData,
      }
    },
  ),
)(MyAccountChangePlanModal)
